import { t } from "@lingui/macro";
import { add, endOfMonth, format, isBefore, startOfMonth, sub } from "date-fns";
import { parse } from "date-fns";
import { useSetAtom, Provider as AtomProvider } from "jotai";
import React, { useState, useEffect, useCallback } from "react";

import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../../../../icons_v2/general";
import { Box, HStack, VStack } from "../../../../primitive";
import { Button } from "../../../Button/Button";
import { Calendar } from "../Calendar";
import { calendarSettingsAtom } from "../Calendar/atoms";
import { MonthNameHeader } from "../Calendar/components/MonthNameHeader";
import { getDateInfoMap } from "./utils/getDateInfoMap";

type TwoCalendarMonthsProps = {
  showTwoCalendars: boolean;
  curStartDate: string;
  curEndDate: string;
  handleUpdateRange: (startDate: string, endDate: string) => void;
  fillEmptyDays: boolean;
  clubWeekStartsOn: NumericalDayOfWeek;
};

export const TwoCalendarMonths = (props: TwoCalendarMonthsProps) => {
  return (
    <AtomProvider>
      <TwoCalendarMonthsInner {...props} />
    </AtomProvider>
  );
};

const TwoCalendarMonthsInner = ({
  showTwoCalendars,
  curStartDate,
  curEndDate,
  handleUpdateRange,
  fillEmptyDays,
  clubWeekStartsOn,
}: TwoCalendarMonthsProps) => {
  const setCalendarSettings = useSetAtom(calendarSettingsAtom);
  const [selectedDates, setSelectedDates] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: curStartDate,
    endDate: curEndDate,
  });

  const [firstMonthObject, setFirstMonthObject] = useState(
    parse(curStartDate, "yyyy-MM-dd", new Date())
  );
  const nextMonthObj = add(firstMonthObject, { months: 1 });

  const handleDatePress = useCallback(
    (date: string) => {
      const hasBothDates = selectedDates.startDate && selectedDates.endDate;
      const isBeforeStartDate =
        selectedDates.startDate &&
        isBefore(
          parse(date, "yyyy-MM-dd", new Date()),
          parse(selectedDates.startDate, "yyyy-MM-dd", new Date())
        );

      if (!selectedDates.startDate || hasBothDates || isBeforeStartDate) {
        setSelectedDates({ startDate: date, endDate: null });
      } else {
        setSelectedDates({ ...selectedDates, endDate: date });
        handleUpdateRange(selectedDates.startDate, date);
      }
    },
    [selectedDates, handleUpdateRange]
  );

  const dateInfo = getDateInfoMap(
    selectedDates,
    firstMonthObject,
    nextMonthObj
  );
  const calendarDateStart = format(
    startOfMonth(firstMonthObject),
    "yyyy-MM-dd"
  );
  const calendarEndStart = format(endOfMonth(nextMonthObj), "yyyy-MM-dd");

  useEffect(() => {
    setCalendarSettings({
      startDate: calendarDateStart,
      endDate: calendarEndStart,
      onDatePress: handleDatePress,
      fillEmptyDays: fillEmptyDays,
      dateInfo: dateInfo ?? null,
      singleDatePicker: false,
      clubWeekStartsOn: clubWeekStartsOn,
    });
  }, [
    calendarDateStart,
    calendarEndStart,
    handleDatePress,
    fillEmptyDays,
    dateInfo,
    clubWeekStartsOn,
    setCalendarSettings,
  ]);

  return (
    <VStack bg="surface-primary" testID={"two-calendar-months-container"}>
      <TwoCalendarHeader
        firstMonthObject={firstMonthObject}
        setFirstMonthObject={setFirstMonthObject}
        nextMonthObj={nextMonthObj}
        showTwoCalendars={showTwoCalendars}
        clubWeekStartsOn={clubWeekStartsOn}
      />
      <HStack justifyContent="space-between" testID="wrapper-around-months">
        <Calendar
          monthDateObj={firstMonthObject}
          isLeft={showTwoCalendars}
          startDate={startOfMonth(firstMonthObject)}
          endDate={endOfMonth(firstMonthObject)}
          showDayOfWeekHeader={true}
          showMonthNameHeader={false}
          fillEmptyDays={fillEmptyDays}
          clubWeekStartsOn={clubWeekStartsOn}
        />
        {showTwoCalendars ? (
          <Calendar
            monthDateObj={nextMonthObj}
            isRight={showTwoCalendars}
            startDate={startOfMonth(nextMonthObj)}
            endDate={endOfMonth(nextMonthObj)}
            showDayOfWeekHeader={true}
            showMonthNameHeader={false}
            fillEmptyDays={fillEmptyDays}
            clubWeekStartsOn={clubWeekStartsOn}
          />
        ) : null}
      </HStack>
    </VStack>
  );
};

type TwoCalendarHeaderProps = {
  firstMonthObject: Date;
  setFirstMonthObject: (date: Date) => void;
  nextMonthObj: Date;
  showTwoCalendars: boolean;
  clubWeekStartsOn: NumericalDayOfWeek;
};

const TwoCalendarHeader = ({
  firstMonthObject,
  setFirstMonthObject,
  nextMonthObj,
  showTwoCalendars,
  clubWeekStartsOn,
}: TwoCalendarHeaderProps) => {
  const decrementMonths = () => {
    const preMonth = sub(firstMonthObject, { months: 1 });
    setFirstMonthObject(preMonth);
  };

  const incrementMonths = () => {
    const nextMonth = add(firstMonthObject, { months: 1 });
    setFirstMonthObject(nextMonth);
  };

  const leftArrowDisabled = false;
  const rightArrowDisabled = false;
  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      paddingBottom={4}
    >
      <Button
        variant={"ghost"}
        size={"tiny"}
        leftIcon={<ChevronLeftIcon />}
        isDisabled={leftArrowDisabled}
        onPress={decrementMonths}
        testID="variable-week-calendar-prev-month"
        accessibilityLabel={t`Previous month button`}
      />
      {!showTwoCalendars ? (
        <MonthNameHeader
          monthDateObj={firstMonthObject}
          clubWeekStartsOn={clubWeekStartsOn}
        />
      ) : (
        <>
          <MonthNameHeader
            monthDateObj={firstMonthObject}
            clubWeekStartsOn={clubWeekStartsOn}
          />
          <Box width={48} />
          <MonthNameHeader
            monthDateObj={nextMonthObj}
            clubWeekStartsOn={clubWeekStartsOn}
          />
        </>
      )}
      <Button
        variant={"ghost"}
        size={"tiny"}
        leftIcon={<ChevronRightIcon />}
        isDisabled={rightArrowDisabled}
        onPress={incrementMonths}
        testID="variable-week-calendar-next-month"
        accessibilityLabel={t`Next month button`}
      />
    </HStack>
  );
};
